import { ui } from "./localization";
import catalog from "../data/models.generated.json";
import type { Language } from "../i18n";
import type { ModelInfo } from "../types";

export const FALLBACK_MODELS = catalog.models as ModelInfo[];
export const CATALOG_SYNCED_AT = catalog.syncedAt;
// 原默认值 @cf/zai-org/glm-5.3-flash 属于 Workers AI 付费档（paid=true），
// 在本账号（Workers Free）上调用会返回 403 "not available on the Workers Free plan"。
// 改用实测可用的免费档模型：262K 上下文 + vision + reasoning + tools。
export const DEFAULT_MODEL_ID = "@cf/qwen/qwen3.8-27b";

const MODEL_DISPLAY_PRIORITY = [
  "@cf/qwen/qwen3.8-27b",
  "@cf/zai-org/glm-5.3-flash",
  "@cf/moonshotai/kimi-k2.7-code",
  "@cf/moonshotai/kimi-k2.6",
  "@cf/deepseek-ai/deepseek-v4-pro-0813",
  "@cf/deepseek-ai/deepseek-v4-flash-0731",
  "@cf/deepseek-ai/deepseek-r1-distill-qwen-32b",
] as const;

export const getModel = (models: ModelInfo[], id: string): ModelInfo =>
  models.find((model) => model.id === id) ?? models[0] ?? FALLBACK_MODELS[0];

export const supportsMultimodalAttachments = (model: ModelInfo): boolean =>
  model.capabilities.includes("vision");

export const formatContextWindow = (tokens: number, language: Language = "en", includeLabel = true): string => {
  if (!tokens) return ui(language, "Context n/a");
  let value: string;
  if (tokens >= 1_000_000) {
    const millions = tokens / 1_000_000;
    value = `${Number.isInteger(millions) ? millions : millions.toFixed(1)}M`;
  } else {
    value = `${Math.round(tokens / 1000)}K`;
  }
  return includeLabel ? ui(language, "{0} context", value) : value;
};

export const formatPrice = (price?: number): string =>
  price == null ? "—" : `$${price < 0.1 ? price.toFixed(3) : price.toFixed(2)}`;

export type ModelSortOrder = "recommended" | "recent" | "output-asc" | "output-desc" | "input-asc" | "input-desc";
export type ModelUseCase = "everyday" | "coding" | "vision";
export type ModelCollection = "all" | "favorites" | "recent";

export const filterModelCollection = (
  models: ModelInfo[],
  collection: ModelCollection,
  useCase: ModelUseCase,
  favoriteIds: string[] = [],
  recentModelIds: string[] = [],
): ModelInfo[] => models.filter((model) =>
  (collection === "all" || (collection === "favorites" ? favoriteIds : recentModelIds).includes(model.id)) &&
  (useCase === "everyday" || model.capabilities.includes(useCase)));

export const sortModels = (
  models: ModelInfo[],
  { favoriteIds = [], recentModelIds = [], order = "recommended" }: {
    favoriteIds?: string[];
    recentModelIds?: string[];
    order?: ModelSortOrder;
  } = {},
): ModelInfo[] => {
  if (order === "recommended") return sortModelsByPrice(models, favoriteIds);
  const recentIndex = new Map(recentModelIds.map((id, index) => [id, index]));
  return [...models].sort((a, b) => {
    if (order === "recent") {
      const difference = (recentIndex.get(a.id) ?? Infinity) - (recentIndex.get(b.id) ?? Infinity);
      if (difference) return difference;
    } else {
      const primary = order.startsWith("input") ? "input" : "output";
      const secondary = primary === "input" ? "output" : "input";
      const direction = order.endsWith("asc") ? 1 : -1;
      // Unknown prices always follow known prices, including descending order.
      for (const key of [primary, secondary] as const) {
        const aPrice = a.prices[key];
        const bPrice = b.prices[key];
        if (aPrice == null && bPrice != null) return 1;
        if (aPrice != null && bPrice == null) return -1;
        if (aPrice != null && bPrice != null && aPrice !== bPrice) return (aPrice - bPrice) * direction;
      }
    }
    return a.provider.localeCompare(b.provider) || a.name.localeCompare(b.name);
  });
};

export const sortModelsByPrice = (
  models: ModelInfo[],
  favoriteIds: string[] = [],
): ModelInfo[] => {
  const sorted = [...models].sort((a, b) => {
    const favoriteDifference = Number(favoriteIds.includes(b.id)) - Number(favoriteIds.includes(a.id));
    if (favoriteDifference) return favoriteDifference;
    const aPriorityIndex = MODEL_DISPLAY_PRIORITY.indexOf(a.id as (typeof MODEL_DISPLAY_PRIORITY)[number]);
    const bPriorityIndex = MODEL_DISPLAY_PRIORITY.indexOf(b.id as (typeof MODEL_DISPLAY_PRIORITY)[number]);
    const aIsPrioritized = aPriorityIndex >= 0;
    const bIsPrioritized = bPriorityIndex >= 0;
    if (aIsPrioritized || bIsPrioritized) {
      if (aIsPrioritized && bIsPrioritized) return aPriorityIndex - bPriorityIndex;
      return aIsPrioritized ? -1 : 1;
    }

    const aHasPrice = a.prices.output != null || a.prices.input != null;
    const bHasPrice = b.prices.output != null || b.prices.input != null;
    if (aHasPrice !== bHasPrice) return Number(bHasPrice) - Number(aHasPrice);

    const outputDifference = (b.prices.output ?? -1) - (a.prices.output ?? -1);
    if (outputDifference) return outputDifference;

    const inputDifference = (b.prices.input ?? -1) - (a.prices.input ?? -1);
    if (inputDifference) return inputDifference;

    return a.provider.localeCompare(b.provider) || a.name.localeCompare(b.name);
  });

  return sorted;
};

export const searchModels = (
  models: ModelInfo[],
  query: string,
  capability: "all" | "reasoning" | "vision" | "tools" | "paid" | "lora",
): ModelInfo[] => {
  const normalized = query.trim().toLowerCase();
  return models.filter((model) => {
    const matchesQuery =
      !normalized ||
      [model.name, model.id, model.provider, model.description]
        .join(" ")
        .toLowerCase()
        .includes(normalized);
    const matchesCapability =
      capability === "all" ||
      (capability === "paid" && model.paid) ||
      (capability === "lora" && model.lora) ||
      model.capabilities.includes(capability as "reasoning" | "vision" | "tools");
    return matchesQuery && matchesCapability;
  });
};
